import React, { useState } from 'react';
import { Button, Flex } from 'antd';
import {
  SavedDateList,
  SavedDateListValidationError,
  savedListErrorMessage,
} from '@/apis/logiblocksAPI';
import SavedListPanelShell from './SavedListPanelShell';
import ListNameField from './ListNameField';
import DateSetEntriesSection from './DateSetEntriesSection';
import PrimaryButton from './PrimaryButton';
import { useCreateSavedDateList } from './dateSetQueries';
import { DateSetValue } from './dateSetTypes';
import { dateSetValueToSavedListInput } from './dateSetUtils';
import styles from './SavedListPanel.module.css';

interface SaveListPanelProps {
  value: DateSetValue;
  onValueChange: (value: DateSetValue) => void;
  onBack: () => void;
  onSaved: (list: SavedDateList) => void;
}

/** "Save as a list": names the current selection and saves it to the workspace. */
const SaveListPanel: React.FC<SaveListPanelProps> = ({
  value,
  onValueChange,
  onBack,
  onSaved,
}) => {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const createList = useCreateSavedDateList();

  const count = value.entries.entries.length;

  const handleSubmit = async () => {
    setError(null);
    try {
      const list = await createList.mutateAsync(
        dateSetValueToSavedListInput(name.trim(), value)
      );
      onSaved(list);
    } catch (e) {
      if (!(e instanceof SavedDateListValidationError)) console.error(e);
      setError(savedListErrorMessage(e, 'Failed to save list'));
    }
  };

  return (
    <SavedListPanelShell title='New list' onBack={onBack}>
      <ListNameField
        value={name}
        onChange={(next) => {
          setName(next);
          setError(null);
        }}
        error={error}
        placeholder='e.g. EU holidays'
      />

      <DateSetEntriesSection value={value} onChange={onValueChange} />

      <Flex justify='flex-end' gap={8} className={styles.footer}>
        <Button onClick={onBack}>Cancel</Button>
        <PrimaryButton
          disabled={!name.trim() || count === 0 || createList.isPending}
          loading={createList.isPending}
          onClick={handleSubmit}
        >
          Submit
        </PrimaryButton>
      </Flex>
    </SavedListPanelShell>
  );
};

export default SaveListPanel;
